import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { newAgreementToken } from '@/lib/crew'
import { jobName } from '@/lib/jobs'
import { parseAnswers, parseQuestions, type Answers, type Question } from '@/lib/questionnaire'
import { describeError, supabase, type Row } from '@/lib/supabase'

export type QuestionnaireTemplate = Omit<Row<'questionnaire_templates'>, 'questions'> & { questions: Question[] }

const keys = {
  all: ['questionnaire-templates'] as const,
  one: (id: string) => ['questionnaire-templates', id] as const,
}

const read = (row: Row<'questionnaire_templates'>): QuestionnaireTemplate => ({ ...row, questions: parseQuestions(row.questions) })

export function useQuestionnaireTemplates() {
  return useQuery({
    queryKey: keys.all,
    queryFn: async (): Promise<QuestionnaireTemplate[]> => {
      const { data, error } = await supabase
        .from('questionnaire_templates')
        .select('*')
        .order('archived')
        .order('is_default', { ascending: false })
        .order('name')
      if (error) throw error
      return data.map(read)
    },
  })
}

export function useQuestionnaireTemplate(id: string) {
  return useQuery({
    queryKey: keys.one(id),
    queryFn: async (): Promise<QuestionnaireTemplate> => {
      const { data, error } = await supabase.from('questionnaire_templates').select('*').eq('id', id).single()
      if (error) throw error
      return read(data)
    },
  })
}

type Saveable = { name: string; intro: string | null; questions: Question[] }

/** Adds a template (blank, or a copy) and returns its id. */
export function useCreateQuestionnaireTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Saveable): Promise<string> => {
      const { data, error } = await supabase.from('questionnaire_templates').insert(input).select('id').single()
      if (error) throw error
      return data.id
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
    onError: (error) => toast.error('Could not add the questionnaire', { description: describeError(error) }),
  })
}

export function useUpdateQuestionnaireTemplate(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<Saveable> & { archived?: boolean }) => {
      const { error } = await supabase.from('questionnaire_templates').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
    onError: (error) => toast.error('Could not save the questionnaire', { description: describeError(error) }),
  })
}

/** Makes one template the default, which unsets the old one first (one default at a time). */
export function useMakeDefaultQuestionnaire() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error: clearError } = await supabase.from('questionnaire_templates').update({ is_default: false }).eq('is_default', true)
      if (clearError) throw clearError
      const { error } = await supabase.from('questionnaire_templates').update({ is_default: true, archived: false }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
    onError: (error) => toast.error('Could not change the default', { description: describeError(error) }),
  })
}

// ---------------------------------------------------------------------------
// Questionnaires sent to couples (29 Oct 2026)
// ---------------------------------------------------------------------------

type ContactBits = { first_name: string; last_name: string | null; email: string | null; phone: string | null }

export type SentQuestionnaire = Omit<Row<'questionnaires'>, 'questions' | 'answers'> & {
  questions: Question[]
  answers: Answers
  /** The client it was sent to. */
  contact: (ContactBits & { id: string }) | null
  /** The project it belongs to, if any. */
  job: {
    id: string
    kind: Row<'jobs'>['kind']
    title: string | null
    event_date: string
    primary_contact: ContactBits | null
    partner_contact: ContactBits | null
  } | null
}

const SENT_SELECT = `*, contact:contacts!questionnaires_contact_id_fkey ( id, first_name, last_name, email, phone ),
  job:jobs!questionnaires_job_id_fkey ( id, kind, title, event_date,
  primary_contact:contacts!jobs_primary_contact_id_fkey ( first_name, last_name, email, phone ),
  partner_contact:contacts!jobs_partner_contact_id_fkey ( first_name, last_name, email, phone ) )`

const sentKeys = {
  all: ['questionnaires'] as const,
  forJob: (jobId: string) => ['questionnaires', 'job', jobId] as const,
  one: (id: string) => ['questionnaires', 'one', id] as const,
}

const readSent = (row: Record<string, unknown>): SentQuestionnaire =>
  ({ ...row, questions: parseQuestions(row.questions), answers: parseAnswers(row.answers) }) as SentQuestionnaire

/** Every questionnaire made for a couple, newest first. */
export function useSentQuestionnaires() {
  return useQuery({
    queryKey: sentKeys.all,
    queryFn: async (): Promise<SentQuestionnaire[]> => {
      const { data, error } = await supabase.from('questionnaires').select(SENT_SELECT).order('created_at', { ascending: false })
      if (error) throw error
      return (data as unknown as Record<string, unknown>[]).map(readSent)
    },
  })
}

export function useJobQuestionnaires(jobId: string) {
  return useQuery({
    queryKey: sentKeys.forJob(jobId),
    queryFn: async (): Promise<SentQuestionnaire[]> => {
      const { data, error } = await supabase.from('questionnaires').select(SENT_SELECT).eq('job_id', jobId).order('created_at', { ascending: false })
      if (error) throw error
      return (data as unknown as Record<string, unknown>[]).map(readSent)
    },
  })
}

export function useSentQuestionnaire(id: string) {
  return useQuery({
    queryKey: sentKeys.one(id),
    queryFn: async (): Promise<SentQuestionnaire> => {
      const { data, error } = await supabase.from('questionnaires').select(SENT_SELECT).eq('id', id).single()
      if (error) throw error
      return readSent(data as unknown as Record<string, unknown>)
    },
  })
}

/** Makes a questionnaire for a client (and maybe a project) from a template: its own copy of the questions. */
export function useCreateQuestionnaire() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      contactId,
      jobId,
      template,
    }: {
      contactId: string
      jobId: string | null
      /** A template, or a blank one (id null). */
      template: Pick<QuestionnaireTemplate, 'name' | 'intro' | 'questions'> & { id: string | null }
    }): Promise<string> => {
      const { data, error } = await supabase
        .from('questionnaires')
        .insert({
          contact_id: contactId,
          job_id: jobId,
          template_id: template.id,
          name: template.name,
          intro: template.intro,
          questions: template.questions,
          token: newAgreementToken(),
        })
        .select('id')
        .single()
      if (error) throw error
      return data.id
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sentKeys.all }),
    onError: (error) => toast.error('Could not make the questionnaire', { description: describeError(error) }),
  })
}

export function useUpdateQuestionnaire() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string; sent_at?: string; completed_at?: null; job_id?: string | null }) => {
      const { error } = await supabase.from('questionnaires').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sentKeys.all }),
    onError: (error) => toast.error('Could not save that', { description: describeError(error) }),
  })
}

export function useDeleteQuestionnaire() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('questionnaires').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sentKeys.all }),
    onError: (error) => toast.error('Could not delete the questionnaire', { description: describeError(error) }),
  })
}

/** A fifteen-minute download link to the questionnaire with its answers. */
export async function questionnairePdfUrl(id: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke<{ signedUrl?: string }>('generate-questionnaire-pdf', {
    body: { questionnaireId: id },
  })
  if (error || !data?.signedUrl) throw new Error('The PDF could not be made. Please try again.')
  return data.signedUrl
}

export function questionnaireLink(token: string): string {
  return `${window.location.origin}/q/${token}`
}

/** Deletes a template. Questionnaires already made from it keep their own copy. */
export function useDeleteQuestionnaireTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('questionnaire_templates').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.all }),
    onError: (error) => toast.error('Could not delete the template', { description: describeError(error) }),
  })
}

/** Who it went to: the couple on a project, or the client's full name. */
export function clientName(row: Pick<SentQuestionnaire, 'contact' | 'job'>): string {
  if (row.job) return jobName(row.job)
  if (row.contact) return [row.contact.first_name, row.contact.last_name].filter(Boolean).join(' ')
  return 'Unknown client'
}
